import {MemoryPrompt, Post} from '../types';

// "This time last year": which tell the Journal card shows, how it says how long ago, and the
// "done it again?" prompt. Pure functions over the memories route's answer.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const MEMORY_KINDS = ['sowed', 'planted', 'harvest'];

const utcDay = (ymd: string): number => Date.UTC(Number(ymd.slice(0, 4)), Number(ymd.slice(5, 7)) - 1, Number(ymd.slice(8, 10)));

/** Whole years between a date and today, to the nearest year. */
export const yearsAgo = (whenLocal: string, today: string): number => Math.round((utcDay(today) - utcDay(whenLocal)) / (365.25 * 86_400_000));

/** Days between the month-day of a date and today's, ignoring the year. */
const monthDayDistance = (whenLocal: string, today: string): number => {
    const a = Date.UTC(2000, Number(whenLocal.slice(5, 7)) - 1, Number(whenLocal.slice(8, 10)));
    const b = Date.UTC(2000, Number(today.slice(5, 7)) - 1, Number(today.slice(8, 10)));
    const d = Math.abs(a - b) / 86_400_000;
    return Math.min(d, 366 - d);
};

/**
 * The one tell for the card: a photo first, then one that made the gardener happy, then a
 * sowing, planting or harvest, then the nearest year, then the nearest day.
 */
export function pickMemory(items: Post[], today: string): Post | null {
    if (items.length === 0) return null;
    const score = (p: Post) => [p.photos.length > 0 ? 0 : 1, p.happy ? 0 : 1, MEMORY_KINDS.includes(p.kind) ? 0 : 1, yearsAgo(p.whenLocal, today), monthDayDistance(p.whenLocal, today)];
    return [...items].sort((a, b) => {
        const sa = score(a);
        const sb = score(b);
        for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return sa[i] - sb[i];
        return 0;
    })[0];
}

const WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five'];

/** "One year ago today", "Two years ago this week", "On this day in 2019", "This week in 2019". */
export function memoryKicker(whenLocal: string, today: string): string {
    const years = Math.max(1, yearsAgo(whenLocal, today));
    const sameDay = whenLocal.slice(5, 10) === today.slice(5, 10);
    if (years < WORDS.length) return `${WORDS[years]} year${years === 1 ? '' : 's'} ago ${sameDay ? 'today' : 'this week'}`;
    return sameDay ? `On this day in ${whenLocal.slice(0, 4)}` : `This week in ${whenLocal.slice(0, 4)}`;
}

export interface YearGroup {
    year: string;
    posts: Post[];
}

/** Newest year first; ascending by date within a year. */
export function groupByYear(items: Post[]): YearGroup[] {
    const groups = new Map<string, Post[]>();
    for (const post of items) {
        const year = post.whenLocal.slice(0, 4);
        groups.set(year, [...(groups.get(year) ?? []), post]);
    }
    return [...groups.entries()]
        .sort(([a], [b]) => (a < b ? 1 : -1))
        .map(([year, posts]) => ({year, posts: posts.sort((a, b) => (a.whenLocal < b.whenLocal ? -1 : 1))}));
}

/** "Last year you planted out Tomato this week." / "In 2019 you sowed Garlic this week." */
export function promptSentence(prompt: MemoryPrompt, today: string): string {
    const years = yearsAgo(prompt.whenLocal, today);
    const when = years === 1 ? 'Last year' : `In ${prompt.whenLocal.slice(0, 4)}`;
    const verb = prompt.kind === 'planted' ? 'planted out' : 'sowed';
    return `${when} you ${verb} ${prompt.plantLabel} this week.`;
}

/** "15 – 29 October" or "28 December – 11 January" for the window around today. */
export function windowLabel(today: string, days: number): string {
    const t = utcDay(today);
    const from = new Date(t - days * 86_400_000);
    const to = new Date(t + days * 86_400_000);
    const fm = MONTHS[from.getUTCMonth()];
    const tm = MONTHS[to.getUTCMonth()];
    return fm === tm ? `${from.getUTCDate()} – ${to.getUTCDate()} ${fm}` : `${from.getUTCDate()} ${fm} – ${to.getUTCDate()} ${tm}`;
}
